import { Component, OnInit } from '@angular/core';
import { AppService } from '../../services/app.service';
import { EmployeeApplicationService } from '../../services/employee-application.service';
import { ClassifierService } from '../../services/classifier.service';
import { IClassifier } from '../../models/Classifier';
import { forkJoin } from 'rxjs';
import { NgbPanelChangeEvent } from '@ng-bootstrap/ng-bootstrap';

interface IGroup {
    id: string;
    title: string;
    code: string;
    items: IEntry[];
    sortOrder: number;
    visible?: boolean;
}

interface IEntry {
    title: string;
    code: string;
    visible?: boolean;
    icon?: string;
    url: {
        route: string,
        params: { [key: string]: any }
    };
}

const cache: {
    types?: string[],
    classifiers?: IClassifier[]
} = {};

let groupState: { [key: string]: boolean } = {};

/**
 * Employee application home page
 */
@Component({
    selector: 'app-employee-application-index',
    templateUrl: './index.component.html',
    styleUrls: ['./application.component.scss']
})
export class EmployeeApplicationIndexComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: EmployeeApplicationService,
        private classifiers: ClassifierService
    ) { }

    groups: IGroup[] = [];
    noItemAvailable: boolean;
    openedGroupIds: string[] = [];

    ngOnInit() {
        const user = this.app.currentUser;

        if (user) {
            if (cache.types) {
                this.init(cache.types, cache.classifiers);
            } else {
                this.app.addLoading(forkJoin([
                    this.service.getTypes(),
                    this.classifiers.get('EmployeeApplicationGroup,EmployeeApplicationType')
                ])).subscribe(([availableTypes, data]) => {
                    cache.types = availableTypes;
                    cache.classifiers = data;

                    this.init(availableTypes, data);
                }, err => {
                    // do nothing
                });
            }
        }
    }

    onPanelChange(event: NgbPanelChangeEvent) {
        groupState[event.panelId] = event.nextState;
    }

    private init(availableTypes: string[], data: IClassifier[]) {
        const rights = this.app.currentUser.rights || [];

        const groups: IGroup[] = data.filter(t => t.Type === 'EmployeeApplicationGroup').map(t => {
            let payload: any;

            try {
                payload = JSON.parse(t.Payload);
            } catch (err) { }

            if (!payload) {
                payload = { SortOrder: 0 };
            }

            return {
                id: t.Id,
                code: t.Code,
                items: [],
                sortOrder: +payload.SortOrder,
                title: t.Value,
                icon: payload?.Icon,
                visible: false
            };
        });

        groups.sort((a, b) => a.sortOrder - b.sortOrder);

        const appTypePrefixLen = 'EmployeeApplicationType'.length;

        data.filter(t => t.Type === 'EmployeeApplicationType').forEach(t => {
            let payload: any;

            try {
                payload = JSON.parse(t.Payload);
            } catch (err) { }

            if (!payload) {
                payload = {};
            }

            const group = groups.find(g => g.code === payload.Group);

            if (group) {
                const type = t.Code.substring(appTypePrefixLen);

                if (availableTypes.indexOf(type) > -1) {
                    group.items.push({
                        code: t.Code,
                        title: t.Value,
                        url: {
                            route: '/employee-applications/add',
                            params: { type }
                        },
                        visible: rights.indexOf(`EMPLOYEE_APPLICATION.LIST.${type}`) > -1
                    });
                }
            }
        });

        groups.forEach(t => {
            t.visible = t.items.some(i => i.visible);
            t.items.sort((a, b) => a.title.localeCompare(b.title));

            if (groupState[t.id] === true) {
                this.openedGroupIds.push(t.id);
            }
        });

        this.groups = groups;
        this.noItemAvailable = !this.groups.some(t => t.visible);
    }
}
